'use client'

import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { TransitionState, TransitionStatus, useTransitionState } from 'react-transition-state'

export interface UseDialogTransitionOptions {
    /** Whether the dialog should be open. Drives the enter/exit animation. */
    open: boolean
    /** Total enter/exit duration in ms. Keep in sync with the transition classes. Defaults to 300. */
    timeout?: number
    /** Fired once the exit animation has finished and the native dialog has closed. */
    onExited?: () => void
}

export interface UseDialogTransitionResult {
    status: TransitionStatus
    isMounted: boolean
    dialogRef: RefObject<HTMLDialogElement>
}

const DEFAULT_TIMEOUT = 300

/**
 * Default per-status animation classes for an animated `Dialog`. Mirrors the legacy `Modal`
 * (300ms): the card scales + fades, and the native `::backdrop` fades alongside it.
 *
 * Returns motion + backdrop-opacity only — the backdrop color and the card surface look belong
 * on `Dialog.Root`'s `className`.
 *
 * @see https://github.com/szhsin/react-transition-state#state-diagram
 */
export const getDialogStatusClassName = (status: TransitionStatus): string => {
    switch (status) {
        case 'preEnter':
            return 'scale-0 opacity-0 backdrop:opacity-0'
        case 'entering':
            return 'scale-100 opacity-100 backdrop:opacity-90 transition-[transform,opacity] duration-300 ease-out backdrop:transition-opacity backdrop:duration-100'
        case 'entered':
            return 'scale-100 opacity-100 backdrop:opacity-90'
        case 'preExit':
            return 'scale-100 opacity-100 backdrop:opacity-90'
        case 'exiting':
        case 'exited':
            return 'scale-0 opacity-50 backdrop:opacity-0 transition-[transform,opacity] duration-300 ease-in backdrop:transition-opacity backdrop:duration-200'
        default:
            return ''
    }
}

/**
 * Per-status animation classes for a dialog that is a bottom sheet on mobile (`DIALOG_MOBILE_SHEET`):
 * below `md` the card slides up from the bottom edge, from `md` up it scales + fades like
 * {@link getDialogStatusClassName}.
 */
export const getSheetDialogStatusClassName = (status: TransitionStatus): string => {
    switch (status) {
        case 'preEnter':
            return 'max-md:translate-y-full md:scale-0 md:opacity-0 backdrop:opacity-0'
        case 'entering':
            return 'translate-y-0 md:scale-100 md:opacity-100 backdrop:opacity-90 transition-[transform,opacity] duration-300 ease-out backdrop:transition-opacity backdrop:duration-100'
        case 'entered':
        case 'preExit':
            return 'translate-y-0 md:scale-100 md:opacity-100 backdrop:opacity-90'
        case 'exiting':
        case 'exited':
            return 'max-md:translate-y-full md:scale-0 md:opacity-50 backdrop:opacity-0 transition-[transform,opacity] duration-300 ease-in backdrop:transition-opacity backdrop:duration-200'
        default:
            return ''
    }
}

/**
 * Drives the enter/exit animation of a native `<dialog>` opened with `showModal()`.
 *
 * `dialog.close()` removes the element from the top layer immediately, so an exit animation never
 * plays if you close on `open=false`. This hook keeps the dialog shown through the exit transition
 * and only calls `close()` once it has finished, using `react-transition-state`'s
 * `preEnter → entering → entered → preExit → exiting → exited` lifecycle.
 *
 * Render the `<dialog ref={dialogRef}>` only while `isMounted`, and apply
 * {@link getDialogStatusClassName}(status) (or your own map) to animate it.
 */
export const useDialogTransition = ({
    open,
    timeout = DEFAULT_TIMEOUT,
    onExited,
}: UseDialogTransitionOptions): UseDialogTransitionResult => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const scrollPositionRef = useRef(0)
    const onExitedRef = useRef(onExited)
    onExitedRef.current = onExited

    const onStateChange = useCallback(({ current }: { current: TransitionState }) => {
        if (current.status === 'exited' || current.status === 'unmounted') {
            dialogRef.current?.close()
            window.scrollTo(0, scrollPositionRef.current)
            onExitedRef.current?.()
        }
    }, [])

    const [{ status, isMounted }, toggle] = useTransitionState({
        timeout,
        preEnter: true,
        preExit: true,
        mountOnEnter: true,
        unmountOnExit: true,
        onStateChange,
    })

    useEffect(() => {
        toggle(open)
    }, [open, toggle])

    // Show the dialog once it has mounted (in `preEnter`), so the entering transition can run.
    useLayoutEffect(() => {
        const dialog = dialogRef.current
        if (isMounted && dialog && !dialog.open) {
            scrollPositionRef.current = window.scrollY
            dialog.showModal()
        }
    }, [isMounted])

    return { status, isMounted, dialogRef }
}
